/** @jsxImportSource theme-ui */
import { jsx } from "theme-ui";
import React, { useState } from "react";
import { Flex, Box, Heading, Text } from "@theme-ui/components";
import Carousel from "react-multi-carousel";
import { Link } from "gatsby";
import moment from "moment";
import { Icon } from "../atoms";
import { typography, colors } from "../../styles/theme";
import { InfoTile, Pagination, SelectionCard } from "../molecules";

const labelStyle = { ...typography.textSm, color: colors.black };

const IntroSubPanel = ({ ques, ans }) => {
  return (
    <Box sx={{ px: "1.188rem", py: 3, width: "15.75rem" }}>
      <Heading as="h3" sx={{ ...typography.h6, mb: "0.5rem" }}>
        {ques}
      </Heading>
      <Box sx={{ ...typography.textMd400 }}>{ans}</Box>
    </Box>
  );
};

const IntroPanel = () => {
  return (
    <Flex
      sx={{
        border: `1px solid ${colors.neutral200}`,
        borderRadius: "1.25rem",
        flexDirection: ["column", "column", "row", "column"],
        height: "max-content",
        minWidth: "15.75rem",
        width: ["15.75rem", "15.75rem", "100%", "15.75rem"],
      }}
    >
      <IntroSubPanel
        ques="How to use this resource?"
        ans="In these pages, you will find information on statutory compliances for
				India. We are currently tracking 100+ acts and 500+ compliances."
      />
      <IntroSubPanel
        ques="Looking for something specific?"
        ans="Use the search box to quickly find the compliance info (e.g. PF ECR)."
      />
      <IntroSubPanel
        ques="Need in-depth information on each act?"
        ans="The acts are categorized as either central (federal) acts or
				state-wise acts. You can also browse the acts by industry."
      />
    </Flex>
  );
};

const ButtonGroup = ({ next, previous, goToSlide, ...rest }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const {
    carouselState: { totalItems, slidesToShow },
  } = rest;

  return (
    <Box className="carousel-button-group" mt={["3rem"]}>
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={slidesToShow}
        onPageChange={(activePage) => {
          setCurrentPage(activePage);
          const isNext = currentPage + 1 === activePage;
          const isPrevious = currentPage - 1 === activePage;
          if (isNext) {
            next();
          } else if (isPrevious) {
            previous();
          } else {
            goToSlide(activePage * slidesToShow - slidesToShow);
          }
        }}
      />
    </Box>
  );
};

const NotificationsList = ({ nodes, title = "Notifications" }) => {
  if (nodes.length <= 0) {
    return null;
  }
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 3,
      slidesToSlide: 3,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 3,
    },
    tablet: {
      breakpoint: { max: 1024, min: 750 },
      items: 2,
      slidesToSlide: 2,
    },
    mobile: {
      breakpoint: { max: 750, min: 0 },
      items: 1,
      slidesToSlide: 1,
    },
  };
  return (
    <Box sx={{ m: ["4rem 0", "4rem 0", "7rem 0", "7rem auto"], maxWidth: "90rem" }}>
      <Heading
        as="h2"
        sx={{ ...typography.h2, textAlign: "center", mb: "1.5rem" }}
      >
        {title}
      </Heading>
      <Carousel
        responsive={responsive}
        arrows={false}
        showDots={false}
        renderButtonGroupOutside={true}
        customButtonGroup={<ButtonGroup />}
        autoPlay={false}
        keyBoardControl
      >
        {nodes.map((item, index) => {
          const path = `/notifications/${item.slug}/`;
          return (
            <Link
              to={path}
              style={{
                color: colors.black,
                textDecoration: "none",
                height: "100%",
                "&:hover": {
                  cursor: "pointer",
                  textDecoration: "none",
                },
              }}
            >
              <Box
                key={`notifcation-${index}`}
                sx={{ margin: "0 0.75rem", height: "100%" }}
              >
                <SelectionCard
                  title={item.title}
                  showRightIcon
                  customStyles={{ maxWidth: ["23.25rem"] }}
                />
              </Box>
            </Link>
          );
        })}
      </Carousel>
    </Box>
  );
};

const ActTag = ({ act }) => {
  return (
    <Box>
      <Icon name="Tag" color={colors.black} size={16} />{" "}
      <Text sx={{ pl: "0.5rem", ...labelStyle }}>{act.shortName}</Text>
    </Box>
  );
};

const NotificationEntry = ({ item }) => {
  const releaseDate = moment(item.date, "YYYY-MM-DD").format("DD MMM, YYYY");
  const path = `/notifications/${item.slug}/`;

  return (
    <InfoTile
      title={item.title}
      content={item.summary}
      topLabel={releaseDate && <Text sx={labelStyle}>{releaseDate}</Text>}
      bottomLabel={item?.act && <ActTag act={item.act} />}
      hasBorder={true}
      url={path}
      customStyles={{
        maxWidth: ["100%", "100%", "21.25rem", "25.25rem"],
      }}
    />
  );
};

export const TopNotifications = ({
  title,
  items,
  hideIntroPanel = false,
  customStyles,
}) => {
  return (
    <Box
      sx={{
        minHeight: "20rem",
        ...customStyles,
      }}
    >
      <Heading as="h2" sx={{ mt: 0, mb: "1.5rem", ...typography.h2 }}>
        {title}
      </Heading>
      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: ["center", "center", "flex-start", "flex-start"],
          gap: "3rem",
          flexDirection: ["column", "column", "column", "row"],
        }}
      >
        <Flex
          sx={{
            flexWrap: "wrap",
            gap: "1.5rem",
          }}
        >
          {items.map((item) => (
            <NotificationEntry key={item.id} item={item} />
          ))}
        </Flex>
        {!hideIntroPanel && <IntroPanel />}
      </Flex>
    </Box>
  );
};

export const NotificationSection = ({ items, title, style }) => {
  const top5Notifications = Array.isArray(items) ? items.slice(0, 5) : [];

  return (
    <Box sx={{ ...style }}>
      <TopNotifications
        items={top5Notifications}
        title={title}
        hideIntroPanel={true}
      />
    </Box>
  );
};

export default NotificationsList;
